import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  Eye,
  AlertTriangle,
  Scale,
  Plus,
  BookOpen
} from 'lucide-react';
import { api } from '../../api/client';
import { getStatusBadge } from '../../utils/helpers';
import Modal from '../../components/common/Modal';
import { showToast } from '../../components/common/Toast';

export default function AdminCalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [selectedLawyerId, setSelectedLawyerId] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedApt, setSelectedApt] = useState(null);

  // Day Manager Modal
  const [selectedDayDate, setSelectedDayDate] = useState(null);
  const [dayOverrides, setDayOverrides] = useState([]);
  const [overrideStatus, setOverrideStatus] = useState('COURT');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideIsAllDay, setOverrideIsAllDay] = useState(true);
  const [overrideStartTime, setOverrideStartTime] = useState('09:00');
  const [overrideEndTime, setOverrideEndTime] = useState('13:00');
  const [savingOverride, setSavingOverride] = useState(false);

  // Month navigation
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);

  const startStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const endStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDayOfMonth.getDate()).padStart(2, '0')}`;

  // Fetch lawyers
  useEffect(() => {
    api.get('/lawyers').then((res) => {
      if (res?.success && res.lawyers) {
        setLawyers(res.lawyers);
        if (res.lawyers[0]) setSelectedLawyerId(String(res.lawyers[0].id));
      }
    });
  }, []);

  const loadData = () => {
    setLoading(true);
    let url = `/appointments/calendar?start=${startStr}&end=${endStr}`;
    if (selectedLawyerId) url += `&lawyerId=${selectedLawyerId}`;

    Promise.all([
      api.get(url),
      selectedLawyerId ? api.get(`/lawyers/${selectedLawyerId}/schedule-overrides`) : Promise.resolve(null),
    ])
      .then(([aptRes, ovRes]) => {
        if (aptRes?.success) setAppointments(aptRes.appointments || []);
        if (ovRes?.success) setDayOverrides(ovRes.overrides || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, [startStr, endStr, selectedLawyerId]);

  const daysInMonth = lastDayOfMonth.getDate();
  const startDayOfWeek = firstDayOfMonth.getDay();

  const monthNames = [
    'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun',
    'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr'
  ];

  // Open Day Click Manager
  const handleDayClick = (dateStr) => {
    setSelectedDayDate(dateStr);
    const existing = dayOverrides.find((o) => o.date === dateStr);
    if (existing) {
      setOverrideStatus(existing.status);
      setOverrideReason(existing.reason || '');
      const isAllDay = existing.isAllDay !== false && (!existing.startTime || !existing.endTime);
      setOverrideIsAllDay(isAllDay);
      setOverrideStartTime(existing.startTime || '09:00');
      setOverrideEndTime(existing.endTime || '13:00');
    } else {
      setOverrideStatus('COURT');
      setOverrideReason('Sud majlisi');
      setOverrideIsAllDay(true);
      setOverrideStartTime('09:00');
      setOverrideEndTime('13:00');
    }
  };

  const handleSaveDayOverride = async (e) => {
    e.preventDefault();
    if (!selectedLawyerId || !selectedDayDate) return;

    setSavingOverride(true);
    try {
      const res = await api.post(`/lawyers/${selectedLawyerId}/schedule-overrides`, {
        date: selectedDayDate,
        status: overrideStatus,
        reason: overrideReason,
        isAllDay: overrideIsAllDay,
        startTime: overrideIsAllDay ? null : overrideStartTime,
        endTime: overrideIsAllDay ? null : overrideEndTime,
      });
      if (res?.success) {
        showToast('Kunlik grafik holati saqlandi!', 'success');
        setSelectedDayDate(null);
        loadData();
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSavingOverride(false);
    }
  };

  const handleDeleteDayOverride = async () => {
    const existing = dayOverrides.find((o) => o.date === selectedDayDate);
    if (!existing) return;

    try {
      const res = await api.delete(`/lawyers/${selectedLawyerId}/schedule-overrides/${existing.id}`);
      if (res?.success) {
        showToast('Maxsus grafik holati bekor qilindi', 'info');
        setSelectedDayDate(null);
        loadData();
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  return (
    <div className="space-y-6 animate-admin-fade-in">
      {/* Month Navigator Header */}
      <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <h2 className="font-serif text-xl font-bold text-slate-900">
            {monthNames[month]} {year}
          </h2>
          <span className="text-xs text-slate-500 font-medium">
            Jami: {appointments.length} ta uchrashuv
          </span>
        </div>

        {/* Lawyer Selector & Date Nav Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={selectedLawyerId}
            onChange={(e) => setSelectedLawyerId(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded text-xs bg-white text-slate-800 font-medium"
          >
            <option value="">Barcha advokatlar</option>
            {lawyers.map((l) => (
              <option key={l.id} value={l.id}>
                {l.fullName}
              </option>
            ))}
          </select>

          <button
            onClick={() => setCurrentDate(new Date())}
            className="px-3 py-1.5 border border-slate-300 rounded text-xs font-semibold hover:bg-slate-50 transition-colors cursor-pointer"
          >
            Bugun
          </button>
          <button
            onClick={prevMonth}
            className="p-1.5 border border-slate-300 rounded hover:bg-slate-50 transition-colors cursor-pointer"
            title="Oldingi oy"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={nextMonth}
            className="p-1.5 border border-slate-300 rounded hover:bg-slate-50 transition-colors cursor-pointer"
            title="Keyingi oy"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-2xs overflow-hidden">
        {/* Days of week header */}
        <div className="grid grid-cols-7 bg-slate-50 text-slate-500 border-b border-slate-200 text-center py-2.5 text-xs font-semibold uppercase tracking-wider">
          <span className="text-rose-600">Yak</span>
          <span>Dush</span>
          <span>Sesh</span>
          <span>Chor</span>
          <span>Pay</span>
          <span>Jum</span>
          <span className="text-blue-600">Shan</span>
        </div>

        {/* Days cells */}
        <div className="grid grid-cols-7 auto-rows-fr divide-x divide-y divide-slate-100 min-h-[500px]">
          {/* Empty cells before start of month */}
          {[...Array(startDayOfWeek)].map((_, i) => (
            <div key={`empty-${i}`} className="bg-slate-50/50 min-h-[100px] p-2" />
          ))}

          {/* Days of current month */}
          {[...Array(daysInMonth)].map((_, i) => {
            const dayNum = i + 1;
            const dayDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
            const dayAppointments = appointments.filter((a) => a.appointmentDate === dayDateStr);
            const override = dayOverrides.find((o) => o.date === dayDateStr);

            const isToday = new Date().toISOString().split('T')[0] === dayDateStr;

            return (
              <div
                key={`day-${dayNum}`}
                onClick={() => handleDayClick(dayDateStr)}
                className={`p-2 min-h-[115px] flex flex-col justify-between transition-colors cursor-pointer ${
                  isToday ? 'bg-amber-50/40' : 'hover:bg-slate-50/80'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs ${isToday ? 'w-6 h-6 rounded-full bg-[#091322] text-[#c5a880] flex items-center justify-center font-bold' : 'text-slate-700'}`}>
                    {dayNum}
                  </span>

                  {override && (
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border leading-tight text-center ${
                      override.status === 'COURT'
                        ? 'bg-rose-50 text-rose-700 border-rose-300'
                        : override.status === 'BUSY'
                        ? 'bg-amber-50 text-amber-700 border-amber-300'
                        : override.status === 'FREE'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                        : 'bg-slate-100 text-slate-700 border-slate-300'
                    }`}>
                      <div>{override.status === 'COURT' ? 'SUD' : override.status === 'BUSY' ? 'BAND' : override.status === 'FREE' ? 'BO‘SH' : 'DAM'}</div>
                      {!override.isAllDay && override.startTime && override.endTime && (
                        <div className="text-[8px] font-mono font-medium opacity-80 mt-0.5 whitespace-nowrap">
                          {override.startTime}-{override.endTime}
                        </div>
                      )}
                    </span>
                  )}
                </div>

                {/* Day Appointments Chips */}
                <div className="space-y-1 flex-1 overflow-y-auto max-h-[85px]">
                  {dayAppointments.map((apt) => {
                    const badge = getStatusBadge(apt.status);
                    return (
                      <div
                        key={apt.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedApt(apt);
                        }}
                        className="p-1 rounded text-[10px] bg-slate-100 hover:bg-slate-200 cursor-pointer border border-slate-200 transition-colors truncate flex items-center"
                        title={`${apt.startTime} - ${apt.clientName}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full mr-1 shrink-0 ${badge.dot}`} />
                        <span className="font-mono mr-1 text-slate-600">{apt.startTime}</span>
                        <span className="font-semibold text-slate-900 truncate">{apt.clientName}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Day Manager Modal */}
      {selectedDayDate && (
        <Modal
          isOpen={Boolean(selectedDayDate)}
          onClose={() => setSelectedDayDate(null)}
          title={`Kun Boshqaruvi: ${selectedDayDate}`}
          maxWidth="max-w-md"
        >
          <div className="space-y-4">
            <form onSubmit={handleSaveDayOverride} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Advokat Grafik Holati (Schedule Status):
                </label>
                <select
                  value={overrideStatus}
                  onChange={(e) => setOverrideStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-xs bg-white font-medium"
                >
                  <option value="FREE">Bo‘sh (Qabul ochiq)</option>
                  <option value="COURT">Sudi bor (Sud majlisi)</option>
                  <option value="BUSY">Ishi bor (Band)</option>
                  <option value="OFF">Dam olish kuni (Qabul yopiq)</option>
                </select>
              </div>

              {overrideStatus !== 'FREE' && (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2.5">
                  <label className="block text-xs font-semibold text-slate-700">
                    Bandlik vaqti (Vaqt tanlash):
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setOverrideIsAllDay(true)}
                      className={`px-2.5 py-1.5 text-xs rounded border text-center font-medium transition-all cursor-pointer ${
                        overrideIsAllDay
                          ? 'bg-[#091322] text-[#c5a880] border-[#091322] shadow-sm'
                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                      }`}
                    >
                      Butun kun (Kun bo‘yi)
                    </button>
                    <button
                      type="button"
                      onClick={() => setOverrideIsAllDay(false)}
                      className={`px-2.5 py-1.5 text-xs rounded border text-center font-medium transition-all cursor-pointer ${
                        !overrideIsAllDay
                          ? 'bg-[#091322] text-[#c5a880] border-[#091322] shadow-sm'
                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                      }`}
                    >
                      Belgilangan soatlar
                    </button>
                  </div>

                  {!overrideIsAllDay && (
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-1">
                          Boshlanish vaqti:
                        </label>
                        <input
                          type="time"
                          value={overrideStartTime}
                          onChange={(e) => setOverrideStartTime(e.target.value)}
                          className="w-full px-2.5 py-1.5 border border-slate-300 rounded text-xs bg-white font-mono"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-slate-600 mb-1">
                          Tugash vaqti:
                        </label>
                        <input
                          type="time"
                          value={overrideEndTime}
                          onChange={(e) => setOverrideEndTime(e.target.value)}
                          className="w-full px-2.5 py-1.5 border border-slate-300 rounded text-xs bg-white font-mono"
                          required
                        />
                      </div>
                      <p className="col-span-2 text-[10px] text-slate-500 italic">
                        * Belgilangan soatlar orasida qabul yopiladi, qolgan soatlarda esa mijozlar qabulga yozila oladi.
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Sabab yoki Sud Joyi:
                </label>
                <input
                  type="text"
                  placeholder="Masalan: Jizzax viloyat sudi, sudya B.Karimov"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-xs bg-white"
                />
              </div>

              <div className="flex justify-between items-center pt-2">
                {dayOverrides.some((o) => o.date === selectedDayDate) && (
                  <button
                    type="button"
                    onClick={handleDeleteDayOverride}
                    className="text-xs text-rose-600 hover:underline"
                  >
                    Holatni bekor qilish
                  </button>
                )}

                <div className="flex space-x-2 ml-auto">
                  <button
                    type="button"
                    onClick={() => setSelectedDayDate(null)}
                    className="px-3 py-1.5 border border-slate-300 rounded text-xs text-slate-700"
                  >
                    Yopish
                  </button>
                  <button
                    type="submit"
                    disabled={savingOverride}
                    className="px-4 py-1.5 bg-[#091322] text-[#c5a880] rounded text-xs font-semibold cursor-pointer"
                  >
                    {savingOverride ? 'Saqlanmoqda...' : 'Saqlash'}
                  </button>
                </div>
              </div>
            </form>

            {/* List appointments on this day */}
            <div className="pt-3 border-t border-slate-200">
              <span className="text-xs font-bold text-slate-800 block mb-2">
                Ushbu kungi arizalar:
              </span>
              {appointments.filter((a) => a.appointmentDate === selectedDayDate).length === 0 ? (
                <p className="text-xs text-slate-400">Hech qanday ariza yo‘q</p>
              ) : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {appointments
                    .filter((a) => a.appointmentDate === selectedDayDate)
                    .map((a) => (
                      <div
                        key={a.id}
                        onClick={() => setSelectedApt(a)}
                        className="p-2 rounded bg-slate-50 border border-slate-200 text-xs flex items-center justify-between cursor-pointer hover:bg-slate-100"
                      >
                        <div>
                          <span className="font-semibold text-slate-900">{a.clientName}</span>
                          <span className="font-mono text-slate-500 ml-2">{a.startTime}</span>
                        </div>
                        <span className={`px-2 py-0.2 rounded text-[10px] font-bold border ${getStatusBadge(a.status).classes}`}>
                          {a.status}
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Appointment Detail Modal */}
      {selectedApt && (
        <Modal
          isOpen={Boolean(selectedApt)}
          onClose={() => setSelectedApt(null)}
          title={`Uchrashuv: ${selectedApt.bookingReference}`}
          maxWidth="max-w-md"
        >
          <div className="space-y-3 text-xs">
            <div className="flex justify-between border-b pb-2">
              <span className="text-slate-500">Holat:</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${getStatusBadge(selectedApt.status).classes}`}>
                {getStatusBadge(selectedApt.status).label}
              </span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-slate-500">Sana & Vaqt:</span>
              <span className="font-semibold text-slate-900 font-mono">
                {selectedApt.appointmentDate} soat {selectedApt.startTime} - {selectedApt.endTime}
              </span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-slate-500">Mijoz:</span>
              <span className="font-semibold text-slate-900">{selectedApt.clientName}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-slate-500">Telefon:</span>
              <span className="font-mono text-slate-800">{selectedApt.clientPhone}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-slate-500">Advokat:</span>
              <span className="font-semibold text-slate-900">{selectedApt.lawyer?.fullName}</span>
            </div>
            {selectedApt.clientReason && (
              <div className="pt-2">
                <span className="text-slate-500 block mb-1">Murojaat sababi:</span>
                <p className="p-2.5 rounded bg-slate-50 border border-slate-200 italic text-slate-800 text-[11px]">
                  "{selectedApt.clientReason}"
                </p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
